import { useEffect } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { chargerResumeMentions } from '@/api/inbox'
import { useProfile } from '@/api/profile'
import { voirMentions } from '@/lib/ouvrirConversation'

const CLE = 'callizy_toast_mentions'

/**
 * À l'ouverture du dashboard : s'il reste des mentions non lues (reçues
 * pendant l'absence), un toast, une fois par session du navigateur.
 * « Voir » ouvre la Boîte de réception sur le filtre Mentions.
 */
export function useToastMentions() {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const moiId = profile?.id ?? null

  useEffect(() => {
    if (moiId === null) return
    let annule = false
    const verifier = () => {
      try {
        if (sessionStorage.getItem(CLE)) return
      } catch {
        /* stockage indisponible : le toast peut revenir, rien de grave */
      }
      chargerResumeMentions()
        .then(({ threads, authors }) => {
          // « Déjà montré » seulement une fois le toast affiché : un premier
          // chargement sans mention ne doit pas bloquer celles qui arrivent
          // pendant une veille, onglet ouvert.
          if (annule || threads < 1) return
          try {
            sessionStorage.setItem(CLE, '1')
          } catch {
            /* idem */
          }
          const titre =
            authors.length === 1
              ? t('inbox.team.mentionsAbsenceOne', { count: threads, name: authors[0] })
              : t('inbox.team.mentionsAbsenceMany', { count: threads })
          toast(titre, {
            duration: 10000,
            action: { label: t('inbox.team.see', 'Voir'), onClick: voirMentions },
          })
        })
        .catch(() => undefined)
    }
    // Au chargement, puis au retour sur l'onglet (réveil de l'ordinateur) :
    // le flux en direct ne rejoue pas ce qui est arrivé pendant la coupure.
    const auRetour = () => document.visibilityState === 'visible' && verifier()
    verifier()
    document.addEventListener('visibilitychange', auRetour)
    return () => {
      annule = true
      document.removeEventListener('visibilitychange', auRetour)
    }
  }, [moiId, t])
}
